import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { Flame, Trophy, Target, Sparkles, ArrowRight } from "lucide-react";
import PageHeader from "@/components/app/PageHeader";
import { isPremium } from "@/lib/premium";

const CARD_META = [
  { sys: "hiragana", labelKey: "hiraMastered", total: 104, bar: "from-sakura to-peach", tint: "bg-sakura/30" },
  { sys: "katakana", labelKey: "kataMastered", total: 104, bar: "from-ramune to-sky", tint: "bg-ramune/40" },
  { sys: "kanji", labelKey: "kanjiMastered", total: 137, bar: "from-matcha to-mint", tint: "bg-matcha/50" },
];

export default function DashboardPage() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const [progress, setProgress] = useState([]);
  const [loadingProg, setLoadingProg] = useState(true);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/progress").then(({ data }) => setProgress(data)).finally(() => setLoadingProg(false));
  }, [user]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-10 text-ink-soft">{t("loading")}</div>;
  if (user === false) return <Navigate to="/login" replace />;

  const premium = isPremium(user);
  const planLabel = t(user.premium?.plan === "lifetime" ? "planLifetime" : user.premium?.plan === "monthly" ? "planMonthly" : "planFree");
  const counts = progress.reduce((acc, p) => ({ ...acc, [p.system]: (acc[p.system] || 0) + 1 }), {});

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <PageHeader eyebrow={t("yourDashboard")} title={<>おかえり, {user.name || "friend"}!</>}
        right={<div className="flex flex-wrap gap-2">
          <span className="kawaii-chip !text-sm !px-4 !py-2" data-testid="streak-chip"><Flame className="w-4 h-4 text-sakura-deep" strokeWidth={2.5} /> {progress.length} {t("charsPracticed")}</span>
          <span className={`kawaii-chip !text-sm !px-4 !py-2 ${premium ? "gold-badge" : ""}`} data-testid="plan-chip"><Sparkles className="w-4 h-4" strokeWidth={2.5} /> {planLabel}</span>
        </div>} />

      {loadingProg ? <div className="text-ink-soft">{t("loading")}</div> : (
        <div className="grid md:grid-cols-3 gap-5">
          {CARD_META.map((c, i) => {
            const value = counts[c.sys] || 0;
            const pct = Math.min(100, Math.round((value / c.total) * 100));
            return (
              <div key={c.sys} className={`kawaii-card p-7 rise-in rise-in-${i + 1}`} data-testid={`progress-card-${c.sys}`}>
                <div className={`inline-flex items-center justify-center w-11 h-11 rounded-2xl ${c.tint} mb-4`}><Target className="w-5 h-5 text-ink" strokeWidth={2.5} /></div>
                <div className="font-display text-4xl text-ink">{value}<span className="text-lg text-ink-soft"> / {c.total}</span></div>
                <div className="font-extrabold mt-1 text-ink">{t(c.labelKey)}</div>
                <div className="mt-5 h-3 rounded-full bg-ink/5 overflow-hidden">
                  <div className={`h-full rounded-full bg-gradient-to-r ${c.bar} transition-all duration-1000`} style={{ width: `${pct}%` }} />
                </div>
                <div className="text-xs font-extrabold mt-2 text-ink-soft">{pct}%</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 grid md:grid-cols-2 gap-5">
        <div className="kawaii-card p-7">
          <div className="flex items-center gap-2 mb-2"><Flame className="w-5 h-5 text-sakura-deep" strokeWidth={2.5} /><div className="font-display text-xl text-ink">{t("dailyChallenge")}</div></div>
          <p className="text-sm text-ink-soft mb-5">{t("dailyChallengeBody")}</p>
          <div className="flex gap-3 mb-5">
            {["日", "月", "火"].map((c) => (
              <div key={c} className="w-16 h-16 rounded-2xl bg-white/90 border border-white shadow-soft flex items-center justify-center"><span className="font-jp text-3xl text-ink">{c}</span></div>
            ))}
          </div>
          <Link to="/practice" className="kawaii-btn kawaii-btn-ink kawaii-btn-sm">{t("startChallenge")} <ArrowRight className="w-4 h-4" strokeWidth={2.5} /></Link>
        </div>

        <div className={`kawaii-card p-7 ${premium ? "" : "shimmer-border"}`} data-testid="dashboard-premium-card">
          <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5 text-sakura-deep" strokeWidth={2.5} /><div className="font-display text-xl text-ink">{t("premium")}</div></div>
          <p className="text-sm text-ink-soft mb-5">{premium ? t("youArePremium") : t("premiumTeaserBody")}</p>
          <div className="flex flex-wrap gap-2">
            <Link to="/pdf" className="kawaii-btn kawaii-btn-sm">{t("pdfSheets")}</Link>
            {!premium && <Link to="/pricing" data-testid="dashboard-pricing-btn" className="kawaii-btn kawaii-btn-primary kawaii-btn-sm">{t("viewPlans")}</Link>}
          </div>
        </div>
      </div>

      <div className="mt-8 kawaii-card p-7">
        <div className="flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-ink-soft" strokeWidth={2.5} /><div className="font-display text-xl text-ink">{t("recentlyMastered")}</div></div>
        {progress.length === 0 ? (
          <div className="text-ink-soft">{t("nothingYet")} <Link to="/practice" className="underline font-extrabold text-ink">{t("startDeck")}</Link>.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {progress.slice(-30).reverse().map((p) => (
              <span key={p.key} className="kawaii-chip !bg-yuzu/60"><span className="font-jp text-base">{p.character}</span> · {p.system}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
